import { useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useDocument, useRestoreDocument } from "../api/documents";
import { useRenameRevision, useRevision, useRevisions } from "../api/revisions";
import type { Revision } from "../api/types";
import { granted } from "../auth/AbilityGate";
import { LazyMarkdown } from "../documents/LazyMarkdown";
import { PageHeader } from "../layout/PageHeader";
import { cx } from "../lib/cx";
import { formatAbsoluteTime, formatRelativeTime } from "../lib/time";
import { Button } from "../ui/Button";
import { Dialog } from "../ui/Dialog";
import { inputClassName } from "../ui/Field";
import { useToast } from "../ui/toast-context";
import "./diff.css";
import { diffDocuments, hasChanges } from "./diff";
import { downloadRevision } from "./export";

function revisionLabel(revision: Revision): string {
  if (revision.name !== null && revision.name !== "") return revision.name;
  return revision.createdBy?.name ?? "Unknown author";
}

/**
 * `/doc/:slug/history[/:revisionId]`. The panel lists every revision; choosing
 * one shows its content read-only beside a diff against the revision before it
 * — or against `?compareTo=`, when a comparison was linked to directly.
 */
export function DocumentHistoryScreen(): ReactNode {
  const { slug, revisionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const documentQuery = useDocument(slug);
  const document = documentQuery.data?.document;
  const abilities = documentQuery.data?.abilities ?? {};
  const revisions = useRevisions(document?.id, document !== undefined);
  const list = revisions.data ?? [];

  const selectedId = revisionId ?? list[0]?.id;
  const selectedIndex = list.findIndex((entry) => entry.id === selectedId);
  const compareTo = searchParams.get("compareTo") ?? list[selectedIndex + 1]?.id;

  const selected = useRevision(selectedId);
  const previous = useRevision(compareTo ?? undefined);

  const restore = useRestoreDocument();
  const rename = useRenameRevision(document?.id ?? "");
  const [renaming, setRenaming] = useState<Revision | null>(null);
  const [name, setName] = useState("");

  const report = (error: Error): void => {
    toast.show(error.message, "error");
  };

  if (documentQuery.isPending) {
    return (
      <p role="status" className="text-sm text-muted">
        Loading history…
      </p>
    );
  }
  if (documentQuery.isError || document === undefined) {
    return (
      <div className="max-w-3xl">
        <h2 className="text-2xl font-semibold">History</h2>
        <p role="alert" className="mt-2 text-sm text-danger">
          {documentQuery.error?.message ?? "This document could not be loaded."}
        </p>
      </div>
    );
  }

  const revision = selected.data;
  const diffHtml =
    revision === undefined
      ? null
      : diffDocuments({
          beforeTitle: previous.data?.title ?? revision.title,
          afterTitle: revision.title,
          before: previous.data?.text ?? "",
          after: revision.text ?? "",
        });
  const changed =
    revision !== undefined &&
    hasChanges({
      beforeTitle: previous.data?.title ?? revision.title,
      afterTitle: revision.title,
      before: previous.data?.text ?? "",
      after: revision.text ?? "",
    });

  return (
    <>
      <PageHeader>
        <h1 className="min-w-0 flex-1 truncate text-base font-semibold">
          History — {document.title === "" ? "Untitled" : document.title}
        </h1>
        <Link
          to={document.url}
          className="shrink-0 rounded-md px-2 py-1 text-sm text-muted hover:bg-hover hover:text-ink"
        >
          Back to document
        </Link>
      </PageHeader>

      <div className="flex gap-6">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-semibold">History</h2>

          {revisions.isPending ? (
            <p role="status" className="mt-4 text-sm text-muted">
              Loading revisions…
            </p>
          ) : list.length === 0 ? (
            <p className="mt-4 text-sm text-muted">This document has no revisions yet.</p>
          ) : revision === undefined ? (
            <p role="status" className="mt-4 text-sm text-muted">
              Loading revision…
            </p>
          ) : (
            <>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  disabled={!granted(abilities, "update") || restore.isPending}
                  onClick={() => {
                    restore.mutate(
                      { id: document.id, revisionId: revision.id },
                      {
                        onSuccess: () => {
                          toast.show("Document restored to this revision");
                          void navigate(document.url);
                        },
                        onError: report,
                      },
                    );
                  }}
                >
                  Restore this revision
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setRenaming(revision);
                    setName(revision.name ?? "");
                  }}
                >
                  Rename
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    void downloadRevision(revision).catch(report);
                  }}
                >
                  Download markdown
                </Button>
              </div>

              <section aria-label="Changes" className="mt-6">
                <h3 className="text-xs font-semibold tracking-wide text-muted uppercase">
                  Changes
                </h3>
                {changed ? (
                  <div
                    className="mt-2"
                    data-testid="revision-diff"
                    // The fragment is produced by our own diff, which escapes
                    // every piece of document text it embeds.
                    dangerouslySetInnerHTML={{ __html: diffHtml ?? "" }}
                  />
                ) : (
                  <p className="mt-2 text-sm text-muted">
                    Nothing changed between these two revisions.
                  </p>
                )}
              </section>

              <section aria-label="Revision content" className="mt-8 border-t border-line pt-4">
                <h3 className="text-xs font-semibold tracking-wide text-muted uppercase">
                  Content
                </h3>
                <LazyMarkdown value={revision.text ?? ""} />
              </section>
            </>
          )}
        </div>

        <aside aria-label="Revisions" className="w-64 shrink-0 border-l border-line pl-4">
          <h2 className="mb-2 text-xs font-semibold tracking-wide text-muted uppercase">
            Revisions
          </h2>
          <ul aria-label="Revision list" className="flex flex-col gap-1">
            {list.map((entry) => (
              <li key={entry.id}>
                <Link
                  to={`${document.url}/history/${entry.id}`}
                  aria-current={entry.id === selectedId ? "true" : undefined}
                  className={cx(
                    "block rounded-md px-2 py-1 text-sm",
                    entry.id === selectedId
                      ? "bg-hover font-medium text-ink"
                      : "text-muted hover:bg-hover hover:text-ink",
                  )}
                >
                  <span className="block truncate">{revisionLabel(entry)}</span>
                  <time
                    dateTime={entry.createdAt}
                    title={formatAbsoluteTime(entry.createdAt)}
                    className="text-xs text-muted"
                  >
                    {formatRelativeTime(entry.createdAt)}
                  </time>
                </Link>
              </li>
            ))}
          </ul>
        </aside>
      </div>

      <Dialog
        open={renaming !== null}
        onClose={() => {
          setRenaming(null);
        }}
        title="Name this revision"
        width="sm"
      >
        <label className="flex flex-col gap-1 text-sm">
          Name
          <input
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            className={inputClassName}
          />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              setRenaming(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={rename.isPending}
            onClick={() => {
              if (renaming === null) return;
              rename.mutate(
                { id: renaming.id, name: name.trim() === "" ? null : name.trim() },
                {
                  onSuccess: () => {
                    toast.show("Revision renamed");
                    setRenaming(null);
                  },
                  onError: report,
                },
              );
            }}
          >
            Save
          </Button>
        </div>
      </Dialog>
    </>
  );
}
